import { useCallback, useEffect, useMemo, useReducer, useState } from 'react'
import { Trans, useLingui } from '@lingui/react/macro'
import Input from '../../ui/elements/input'
import Label from '../../ui/elements/label'
import CsvImporter, { FileMapping } from './csv-importer'
import proj from 'proj4'
import useDebounce from '../../../hooks/use-debounce'
import { featureCollection, point } from '@turf/helpers'
import ValidationMessage from '../../ui/shared/validation-message'
import { Extent, getCenter } from 'ol/extent'
import ModalOverlay, {
  ModalOverlayContent,
  ModalOverlayFooter,
} from '../../ui/shared/overlays/modal-overlay'
import Button from '../../ui/elements/button'
import { getDistance } from 'ol/sphere'
import { distanceToString } from '../../../units'
import { Gcps } from './types'
import { fetchEpsg, wktToProj } from '../../../utils/geo-utils'

interface ProjectionWithMetadata extends InstanceType<typeof proj.Proj> {
  GEOGCS?: { name?: string }
  srsCode?: string
}

type ProjectionState =
  | 'loading'
  | 'unknown'
  | {
      projection: ProjectionWithMetadata
      name: string
    }

interface ImportPointsProps {
  bounds: Extent
  onImport: (sourceCrs: string, points: Gcps) => void
  onCancel: () => void
}

interface MapPointsProps extends ImportPointsProps {
  file: File
  fetchEpsg: (epsg: number) => Promise<string>
}

type ProjectionAlias = { epsg: number; name: string }

const epsgAliases: Record<number, ProjectionAlias> = {
  // Name of the coordinate reference system
  // eslint-disable-next-line lingui/no-unlocalized-strings
  5845: { epsg: 3006, name: 'SWEREF99 TM + RH2000' },
}

export default function ImportPoints(props: ImportPointsProps) {
  const [file, setFile] = useState<File>()

  return !file ? (
    <FilePicker onFileSelected={setFile} />
  ) : (
    <MapPoints {...props} file={file} fetchEpsg={fetchEpsg} />
  )
}

export function MapPoints({
  bounds,
  onImport,
  onCancel,
  file,
  fetchEpsg,
}: MapPointsProps) {
  const { t } = useLingui()
  const pointsMappings = usePointsMappings()
  const [selectedMapping, setSelectedMapping] = useState<
    FileMapping | undefined
  >(undefined)
  const [epsg, setEpsg] = useState<number>(4326)
  const [knownEpsg, setKnownEpsg] = useReducer(
    (
      state: Record<number, ProjectionState>,
      { epsg, crs }: { epsg: number; crs: ProjectionState }
    ) => ({ ...state, [epsg]: crs }),
    {}
  )

  const debouncedEpsg = useDebounce(epsg, 500)
  useEffect(() => {
    // 2000 is the lowest known EPSG number, so don't fetch if it's lower (for example "23"
    // and other values that might occur while the user is typing (slowly)).
    // Source: https://spatialreference.org/ref/
    if (!knownEpsg[debouncedEpsg]) {
      if (debouncedEpsg >= 2000 && debouncedEpsg <= 32766) {
        setKnownEpsg({ epsg: debouncedEpsg, crs: 'loading' })

        const alias = epsgAliases[debouncedEpsg]

        fetchEpsg(alias ? alias.epsg : debouncedEpsg)
          .then(wkt => {
            const projection = wktToProj(wkt)
            if (projection) {
              setKnownEpsg({
                epsg: debouncedEpsg,
                crs: {
                  projection,
                  name:
                    alias?.name ||
                    projection.GEOGCS?.name ||
                    projection.srsCode ||
                    debouncedEpsg.toString(),
                },
              })
            } else {
              setKnownEpsg({ epsg: debouncedEpsg, crs: 'unknown' })
            }
          })
          .catch(err => {
            console.warn(`Failed to get EPSG:${debouncedEpsg}`, err)
            setKnownEpsg({ epsg: debouncedEpsg, crs: 'unknown' })
          })
      } else {
        setKnownEpsg({ epsg: debouncedEpsg, crs: 'unknown' })
      }
    }
  }, [debouncedEpsg, knownEpsg, fetchEpsg])

  const currentEpsg = knownEpsg[debouncedEpsg]

  const result = useMemo<Gcps | undefined>(() => {
    if (
      selectedMapping?.mappedCsv &&
      currentEpsg &&
      currentEpsg !== 'loading' &&
      currentEpsg !== 'unknown'
    ) {
      try {
        return featureCollection(
          selectedMapping.mappedCsv
            .filter(({ lat, lng, z }) =>
              [lat, lng, z].every(x => x != null && !isNaN(x as any))
            )
            .map((row, i) => {
              const sourceCoordinates = [row.lng, row.lat, row.z].map(Number)
              // The map position is horizontal; the source height is kept in
              // sourceCoordinates
              const [x, y] = proj(currentEpsg.projection, proj.WGS84, [
                sourceCoordinates[0],
                sourceCoordinates[1],
              ])
              if (!Number.isFinite(x) || !Number.isFinite(y)) {
                throw new Error(`Could not transform ${sourceCoordinates}`)
              }
              const number = i + 1
              return point([x, y], {
                ...row,
                id: row.name,
                name: row.name || t`GCP #${number}`,
                numberMarkers: 0,
                sourceCoordinates,
              })
            })
        )
      } catch (e) {
        console.warn(e)
        return undefined
      }
    }
  }, [currentEpsg, selectedMapping, t])

  const hasValidMapping = ['lat', 'lng', 'z', 'name'].every(
    attribute =>
      selectedMapping && selectedMapping.mappedColumns.indexOf(attribute) >= 0
  )
  const hasValidNames = useMemo(() => {
    const names = result?.features.map(f => f.properties.name)
    return new Set(names).size === result?.features.length
  }, [result])
  const { hasValidCoords, maxDistance } = useMemo(() => {
    const center = getCenter(bounds)

    if (result && result.features.length > 0) {
      const hasValidCoords = result.features.every(
        ({ geometry: { coordinates } }) =>
          coordinates[0] >= bounds[0] &&
          coordinates[0] <= bounds[2] &&
          coordinates[1] >= bounds[1] &&
          coordinates[1] <= bounds[3]
      )

      return {
        hasValidCoords,
        maxDistance: result.features.reduce(
          (maxDist, feature) =>
            Math.max(
              maxDist,
              getDistance(center, feature.geometry.coordinates)
            ),
          0
        ),
      }
    }

    return { hasValidCoords: false, maxDistance: 0 }
  }, [result, bounds])

  const distance = maxDistance ? distanceToString(maxDistance) : null

  return (
    <ModalOverlay
      title={t`Import GCP`}
      size="large"
      show={true}
      onDismiss={onCancel}>
      <ModalOverlayContent>
        <div className="tw-flex tw-flex-col tw-space-y-4">
          <div>
            <div className="tw-mb-4">
              <ValidationMessage>
                {result ? (
                  !hasValidMapping ? (
                    <span>
                      <Trans>
                        Please map the columns from the imported file to Name,
                        Latitude/Northing, Longitude/Easting and Altitude.
                      </Trans>
                    </span>
                  ) : !hasValidNames ? (
                    <span>
                      <Trans>Please ensure each row has a unique name.</Trans>
                    </span>
                  ) : !hasValidCoords ? (
                    <span>
                      {distance ? (
                        <Trans>
                          The GCP coordinates are not valid or too far from the
                          images (approximately {distance}), check the selected
                          Coordinate Reference System
                        </Trans>
                      ) : (
                        <Trans>
                          The GCP coordinates are not valid or too far from the
                          images, check the selected Coordinate Reference System
                        </Trans>
                      )}
                    </span>
                  ) : null
                ) : null}
              </ValidationMessage>
            </div>
            <div className="tw-w-full tw-overflow-auto">
              <CsvImporter
                file={file}
                columnMappings={pointsMappings}
                onChange={setSelectedMapping}
              />
            </div>
          </div>
          <div>
            <Label htmlFor="epsg">
              <Trans>Coordinate Reference System (EPSG)</Trans>
            </Label>
            <div className="tw-flex tw-flex-row tw-content-center">
              <div>
                <Input
                  id="epsg"
                  type="number"
                  hasValidation={true}
                  value={epsg}
                  onChange={({ target: { value } }) => setEpsg(Number(value))}
                />
              </div>
              <div className="tw-ml-8 tw-text-right tw-my-auto">
                {currentEpsg === 'loading' ? (
                  <i className="fas fa-spinner fa-spin" />
                ) : currentEpsg === 'unknown' ? (
                  <span className="tw-system-gray-400">
                    <Trans>Unknown EPSG</Trans>
                  </span>
                ) : (
                  currentEpsg?.name
                )}
              </div>
            </div>
          </div>
        </div>
      </ModalOverlayContent>
      <ModalOverlayFooter>
        <div className="tw-flex tw-justify-between">
          <Button size="large" onClick={onCancel} className="tw-w-auto">
            <Trans>Cancel</Trans>
          </Button>
          <Button
            action="primary"
            size="large"
            disabled={!hasValidMapping || !hasValidNames || !hasValidCoords}
            className="tw-w-auto"
            onClick={() => result && onImport(`EPSG:${debouncedEpsg}`, result)}>
            <Trans>Import</Trans>
          </Button>
        </div>
      </ModalOverlayFooter>
    </ModalOverlay>
  )
}

/** The GCP file's columns, with the header names each is recognised by. */
function usePointsMappings() {
  const { t } = useLingui()
  return useMemo(
    () => ({
      lat: {
        columnNames: ['latitude', 'lat', 'y', 'northing', 'north'],
        displayName: t`Latitude/Northing`,
      },
      lng: {
        columnNames: [
          'longitude',
          'lng',
          'lon',
          'long',
          'x',
          'easting',
          'east',
        ],
        displayName: t`Longitude/Easting`,
      },
      z: {
        columnNames: ['elevation', 'altitude', 'alt', 'z', 'height'],
        displayName: t`Altitude`,
      },
      name: {
        columnNames: ['name', 'type'],
        displayName: t`Name`,
      },
    }),
    [t]
  )
}

function FilePicker({
  onFileSelected,
}: {
  onFileSelected: (file: File) => void
}) {
  const fileInputRef = useCallback(node => {
    if (node) {
      node.click()
    }
  }, [])

  return (
    <input
      type="file"
      className="tw-hidden"
      style={{
        display:
          'none' /* Because of Bootstrap messing up our styles we need this for now */,
      }}
      ref={fileInputRef}
      onChange={e => {
        const file = e.target.files?.[0]
        if (file) {
          onFileSelected(file)
        }
      }}
      data-testid="gcp-file-picker"
    />
  )
}
